/**
 * Page-truncation warnings from the extraction engine.
 *
 * Anthropic's model API rejects PDFs over 100 pages, so the extraction engine truncates an oversized
 * document to its first N pages and returns a `truncationWarnings` array on the response. These helpers
 * turn that into a reviewer-facing "only the first N pages were read — verify" notice. The warning is
 * shown right after an extract / re-extract; a banner that survives a page reload needs the persisted
 * page-count column (separate follow-up), which isn't available on the response yet.
 */

export interface TruncationWarning {
    filename?: string;
    originalPageCount?: number;
    pagesUsed?: number;
    maxPages?: number;
    message?: string;
}

/** Extract the engine's page-truncation warnings from an extraction response `data` payload. */
export function getTruncationWarnings(data: any): TruncationWarning[] {
    return Array.isArray(data?.truncationWarnings) ? (data.truncationWarnings as TruncationWarning[]) : [];
}

/** One reviewer-facing line per truncated document. Prefers the engine's own message. */
function oneLine(w: TruncationWarning): string {
    if (w.message) return w.message;
    const name = w.filename ?? "A document";
    const pages = w.originalPageCount != null ? `${w.originalPageCount} pages` : "too many pages";
    const cap = w.maxPages ?? 100;
    return `"${name}" is ${pages}, over the ${cap}-page limit — only the first ${w.pagesUsed ?? cap} pages were read. Verify the extraction.`;
}

/**
 * Plain-text warning, safe for SweetAlert's `text:` (rendered as textContent, no HTML injection).
 * Returns null when nothing was truncated.
 */
export function truncationWarningText(data: any): string | null {
    const ws = getTruncationWarnings(data);
    if (!ws.length) return null;
    return ws.map(oneLine).join("\n\n");
}

/** Escape a value reflected into innerHTML (repo rule: every HTML-reflected value passes through escape). */
const esc = (s: string): string =>
    s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

/**
 * Escaped amber warning banner for SweetAlert's `html:` (used where the popup is already HTML, e.g. the
 * FilingReview extraction summary). Empty string when nothing was truncated.
 */
export function truncationWarningHtml(data: any): string {
    const ws = getTruncationWarnings(data);
    if (!ws.length) return "";
    const items = ws.map((w) => `<li>${esc(oneLine(w))}</li>`).join("");
    return (
        `<div style="text-align:left;background:#fff4e5;border:1px solid #ffb74d;border-radius:6px;` +
        `padding:8px 12px;margin-bottom:12px;color:#8a5000;font-size:12.5px">` +
        `<strong>⚠ Large document — first pages only</strong>` +
        `<ul style="margin:6px 0 0 18px;padding:0">${items}</ul></div>`
    );
}
